import type {
	ProjectDirectMessageDto,
	ProjectDirectThreadDto,
	SendProjectDirectMessageAttachmentInput,
} from "@shared/types";
import {
	ArrowLeft,
	ImagePlus,
	MessageSquareText,
	Paperclip,
	Send,
	Sparkles,
} from "lucide-react";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { uploadMultipleFiles } from "@/features/projects/services/projects-api.service";
import { cn } from "@/lib/utils";
import {
	createOrGetProjectDirectThread,
	getProjectDirectMessages,
	getProjectDirectThreads,
	markProjectDirectThreadRead,
	sendProjectDirectMessage,
} from "../services/project-direct-messages-api.service";
import { connectProjectDirectMessagesRealtime } from "../services/project-direct-messages-realtime.service";

interface Props {
	projectId: string;
	onUnreadCountChange?: (count: number) => void;
}

function roleLabel(role: ProjectDirectThreadDto["partner"]["role"]): string {
	switch (role) {
		case "homeowner":
			return "Homeowner";
		case "designer":
			return "Designer";
		case "contractor":
			return "Contractor";
		default:
			return role;
	}
}

function initials(name: string): string {
	return name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

function formatTimestamp(value: string): string {
	return new Date(value).toLocaleTimeString([], {
		hour: "numeric",
		minute: "2-digit",
	});
}

export function ProjectDirectMessagesPanel({
	projectId,
	onUnreadCountChange,
}: Props): React.ReactElement {
	const [threads, setThreads] = useState<ProjectDirectThreadDto[]>([]);
	const [selectedThread, setSelectedThread] =
		useState<ProjectDirectThreadDto | null>(null);
	const [messages, setMessages] = useState<ProjectDirectMessageDto[]>([]);
	const [isLoadingThreads, setIsLoadingThreads] = useState(true);
	const [isLoadingMessages, setIsLoadingMessages] = useState(false);
	const [draft, setDraft] = useState("");
	const [sendError, setSendError] = useState<string | null>(null);
	const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
	const [uploadedAttachments, setUploadedAttachments] = useState<
		SendProjectDirectMessageAttachmentInput[]
	>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [mobileShowingThread, setMobileShowingThread] = useState(false);
	const bottomRef = useRef<HTMLDivElement | null>(null);
	const messageViewportRef = useRef<HTMLDivElement | null>(null);
	const mountedRef = useRef(true);
	const openThreadAbortRef = useRef<number>(0);

	const loadThreads = useCallback(async (): Promise<void> => {
		setIsLoadingThreads(true);
		try {
			const nextThreads = await getProjectDirectThreads(projectId);
			setThreads(nextThreads);
			onUnreadCountChange?.(
				nextThreads.reduce((sum, thread) => sum + thread.unreadCount, 0),
			);
		} finally {
			setIsLoadingThreads(false);
		}
	}, [projectId, onUnreadCountChange]);

	useEffect(() => {
		void loadThreads();
	}, [loadThreads]);

	useEffect(() => {
		if (!selectedThread) return;
		if (typeof bottomRef.current?.scrollIntoView === "function") {
			bottomRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [selectedThread]);

	useEffect(() => {
		let connection: { disconnect: () => void } | null = null;
		let cancelled = false;
		void connectProjectDirectMessagesRealtime({
			projectId,
			onEvent: async (event) => {
				if (cancelled || event.projectId !== projectId) return;
				await loadThreads();
				if (!mountedRef.current) return;
				const currentSelectedId = selectedThread?.id;
				if (currentSelectedId && event.threadId === currentSelectedId) {
					const nextPage = await getProjectDirectMessages(
						projectId,
						currentSelectedId,
					);
					if (!mountedRef.current) return;
					setSelectedThread(nextPage.thread);
					setMessages(nextPage.messages);
				}
			},
			onError: () => undefined,
		})
			.then((value) => {
				connection = value;
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
			connection?.disconnect();
		};
	}, [projectId, selectedThread?.id, loadThreads]);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	async function openThread(thread: ProjectDirectThreadDto): Promise<void> {
		const callId = ++openThreadAbortRef.current;
		setSendError(null);
		setMobileShowingThread(true);
		setIsLoadingMessages(true);
		try {
			const ensuredThread =
				thread.id == null
					? await createOrGetProjectDirectThread(projectId, {
							participantUserId: thread.partner.userId,
						})
					: thread;
			if (callId !== openThreadAbortRef.current || !mountedRef.current) return;
			const page = await getProjectDirectMessages(
				projectId,
				ensuredThread.id as string,
			);
			if (callId !== openThreadAbortRef.current || !mountedRef.current) return;
			setSelectedThread(page.thread);
			setMessages(page.messages);
			setThreads((current) => {
				const updatedThreads = current.map((item) =>
					item.partner.userId === page.thread.partner.userId
						? page.thread
						: item,
				);
				const totalUnread = updatedThreads.reduce(
					(sum: number, item) => sum + (item.unreadCount ?? 0),
					0,
				);
				onUnreadCountChange?.(totalUnread);
				return updatedThreads;
			});
			const lastMessage = page.messages[page.messages.length - 1];
			if (
				lastMessage &&
				callId === openThreadAbortRef.current &&
				mountedRef.current
			) {
				await markProjectDirectThreadRead(projectId, page.thread.id as string, {
					lastReadMessageId: lastMessage.id,
				});
			}
		} finally {
			if (callId === openThreadAbortRef.current) {
				setIsLoadingMessages(false);
			}
		}
	}

	async function handleAttachmentChange(files: FileList | null): Promise<void> {
		const nextFiles = files ? Array.from(files) : [];
		if (nextFiles.length === 0) return;
		setIsUploading(true);
		setSendError(null);
		try {
			const uploadResult = await uploadMultipleFiles(nextFiles, {
				folder: "messages",
				projectId,
			});
			if (!uploadResult.success || !uploadResult.urls) {
				setSendError(uploadResult.error || "Failed to upload files");
				return;
			}
			const urls = uploadResult.urls;
			setAttachmentFiles((current) => [...current, ...nextFiles]);
			setUploadedAttachments((current) => [
				...current,
				...urls.map((url, index) => ({
					fileUrl: url,
					fileName: nextFiles[index]?.name,
					mimeType: nextFiles[index]?.type || "image/*",
					fileSize: nextFiles[index]?.size,
				})),
			]);
		} finally {
			setIsUploading(false);
		}
	}

	async function handleSend(): Promise<void> {
		if (!selectedThread?.id) return;
		if (!draft.trim() && uploadedAttachments.length === 0) return;
		const sendingThreadId = selectedThread.id;
		const sendingPartnerId = selectedThread.partner.userId;
		setSendError(null);
		setIsSending(true);
		const optimisticMessage: ProjectDirectMessageDto = {
			id: `optimistic-${Date.now()}`,
			threadId: sendingThreadId,
			projectId,
			sender: { userId: "me", role: "homeowner", name: "You" },
			content: draft.trim() || null,
			messageType:
				draft.trim() && uploadedAttachments.length > 0
					? "mixed"
					: uploadedAttachments.length > 0
						? "attachment"
						: "text",
			attachments: uploadedAttachments.map((attachment, index) => ({
				id: `upload-${index}`,
				fileUrl: attachment.fileUrl,
				fileName: attachment.fileName || null,
				mimeType: attachment.mimeType,
				fileSize: attachment.fileSize || null,
			})),
			createdAt: new Date().toISOString(),
			isOwnMessage: true,
		};
		setMessages((current) => [...current, optimisticMessage]);
		const result = await sendProjectDirectMessage(projectId, sendingThreadId, {
			content: draft.trim(),
			attachments: uploadedAttachments,
		});
		setIsSending(false);
		if (!mountedRef.current || selectedThread.id !== sendingThreadId) {
			return;
		}
		if (!result.success || !result.message) {
			setMessages((current) =>
				current.filter((message) => message.id !== optimisticMessage.id),
			);
			setSendError(result.error || "Failed to send direct message");
			return;
		}
		setMessages((current) =>
			current.map((message) =>
				// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
				message.id === optimisticMessage.id ? result.message! : message,
			),
		);
		setThreads((current) =>
			current
				.map((thread) =>
					thread.partner.userId === sendingPartnerId
						? {
								...thread,
								id: sendingThreadId,
								lastMessage: {
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									id: result.message!.id,
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									content: result.message!.content,
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									messageType: result.message!.messageType,
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									createdAt: result.message!.createdAt,
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									senderUserId: result.message!.sender.userId,
									// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
									attachmentCount: result.message!.attachments.length,
								},
								// biome-ignore lint/style/noNonNullAssertion: message is guaranteed to exist after success check above
								updatedAt: result.message!.createdAt,
							}
						: thread,
				)
				.sort(
					(a, b) =>
						new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
				),
		);
		setDraft("");
		setAttachmentFiles([]);
		setUploadedAttachments([]);
	}

	function handleDraftKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
		if (event.key !== "Enter" || event.shiftKey) {
			return;
		}

		event.preventDefault();
		void handleSend();
	}

	return (
		<div
			className="h-[calc(100vh-15rem)] min-h-[620px] overflow-hidden shadow-[0_28px_80px_-45px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_50%,transparent)]"
			style={{
				background: "var(--color-dashboard-paper)",
				borderRadius: 11,
				border: "1px solid var(--color-dashboard-border)",
			}}
		>
			<div className="grid h-full grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)]">
				<div
					className={cn(
						"min-h-0",
						mobileShowingThread ? "hidden md:block" : "block",
					)}
				>
					<div
						className="flex h-full flex-col border-r bg-dashboard-paper/70 backdrop-blur"
						style={{ borderColor: "var(--color-dashboard-border)" }}
					>
						<div
							className="border-b px-5 py-4"
							style={{
								borderColor: "var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<div
								className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em]"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								<Sparkles
									className="h-3.5 w-3.5"
									style={{ color: "var(--color-dashboard-accent-dark)" }}
								/>
								Private coordination
							</div>
							<h3
								className="mt-2 text-2xl"
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.01em",
								}}
							>
								Messages
							</h3>
							<p
								className="mt-1 text-sm"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								Start a 1:1 thread with anyone active on this project.
							</p>
						</div>
						<div className="flex-1 overflow-y-auto p-3">
							{isLoadingThreads ? (
								<div className="space-y-3">
									{Array.from({ length: 4 }).map((_, index) => (
										<div
											// biome-ignore lint/suspicious/noArrayIndexKey: skeleton loaders are static and don't change order
											key={`skeleton-thread-${index}`}
											className="flex items-center gap-3 rounded-2xl border border-border/70 p-3"
										>
											<Skeleton className="h-10 w-10 rounded-full" />
											<div className="flex-1 space-y-2">
												<Skeleton className="h-4 w-24" />
												<Skeleton className="h-3 w-40" />
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="space-y-2">
									{threads.map((thread) => {
										const selected =
											selectedThread?.partner.userId === thread.partner.userId;
										return (
											<button
												type="button"
												key={thread.partner.userId}
												onClick={() => void openThread(thread)}
												className={cn(
													"w-full rounded-[15px] border p-3 text-left transition-all duration-200",
													selected
														? "shadow-[0_18px_45px_-28px_color-mix(in_srgb,var(--color-dashboard-accent)_35%,transparent)]"
														: "hover:border-dashboard-warm-200",
												)}
												style={{
													background: selected
														? "var(--color-dashboard-paper)"
														: "color-mix(in srgb, var(--color-dashboard-paper) 80%, transparent)",
													borderColor: selected
														? "var(--color-dashboard-accent)"
														: "var(--color-dashboard-border)",
												}}
											>
												<div className="flex items-start gap-3">
													<Avatar
														className="h-11 w-11"
														style={{
															border: "1px solid var(--color-dashboard-border)",
														}}
													>
														<AvatarImage
															src={
																thread.partner.profilePictureUrl || undefined
															}
														/>
														<AvatarFallback
															style={{
																background: "var(--color-dashboard-divider)",
																color: "var(--color-dashboard-text)",
															}}
														>
															{initials(thread.partner.name)}
														</AvatarFallback>
													</Avatar>
													<div className="min-w-0 flex-1">
														<div className="flex items-center justify-between gap-2">
															<div>
																<p
																	style={{
																		color: "var(--color-dashboard-text)",
																	}}
																>
																	{thread.partner.name}
																</p>
																<p
																	className="text-xs uppercase tracking-[0.18em]"
																	style={{
																		color: "var(--color-dashboard-muted)",
																	}}
																>
																	{roleLabel(thread.partner.role)}
																</p>
															</div>
															{thread.unreadCount > 0 && (
																<span
																	className="rounded-full px-2 py-0.5 text-xs font-semibold"
																	style={{
																		background: "var(--color-dashboard-accent)",
																		color: "var(--color-dashboard-on-fill)",
																	}}
																>
																	{thread.unreadCount}
																</span>
															)}
														</div>
														<p
															className="mt-2 truncate text-sm"
															style={{ color: "var(--color-dashboard-muted)" }}
														>
															{thread.lastMessage?.messageType === "attachment"
																? "Sent an image"
																: thread.lastMessage?.content ||
																	"No private messages yet"}
														</p>
													</div>
												</div>
											</button>
										);
									})}
								</div>
							)}
						</div>
					</div>
				</div>
				<div
					className={cn(
						"min-h-0",
						mobileShowingThread ? "block" : "hidden md:block",
					)}
				>
					{selectedThread ? (
						<div
							className="flex h-full min-h-0 flex-col"
							style={{
								background:
									"radial-gradient(circle at top, color-mix(in srgb, var(--color-dashboard-warm-100) 70%, transparent), transparent 34%), linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-surface) 100%)",
							}}
						>
							<div
								className="border-b px-5 py-4 backdrop-blur"
								style={{
									borderColor: "var(--color-dashboard-border)",
									background:
										"color-mix(in srgb, var(--color-dashboard-paper) 80%, transparent)",
								}}
							>
								<div className="flex items-center gap-3">
									<Button
										variant="ghost"
										size="sm"
										className="md:hidden"
										onClick={() => setMobileShowingThread(false)}
									>
										<ArrowLeft className="h-4 w-4" />
									</Button>
									<Avatar
										className="h-11 w-11"
										style={{
											border: "1px solid var(--color-dashboard-border)",
										}}
									>
										<AvatarImage
											src={
												selectedThread.partner.profilePictureUrl || undefined
											}
										/>
										<AvatarFallback
											style={{
												background: "var(--color-dashboard-divider)",
												color: "var(--color-dashboard-text)",
											}}
										>
											{initials(selectedThread.partner.name)}
										</AvatarFallback>
									</Avatar>
									<div>
										<p
											className="font-semibold"
											style={{ color: "var(--color-dashboard-text)" }}
										>
											{selectedThread.partner.name}
										</p>
										<p
											className="text-sm"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											{roleLabel(selectedThread.partner.role)}
										</p>
									</div>
								</div>
							</div>
							<div
								ref={messageViewportRef}
								data-testid="project-direct-messages-scroll-area"
								className="min-h-0 flex-1 overflow-y-auto px-5 py-5"
							>
								{isLoadingMessages ? (
									<div className="space-y-4">
										{Array.from({ length: 4 }).map((_, index) => (
											<div
												// biome-ignore lint/suspicious/noArrayIndexKey: skeleton loaders are static and don't change order
												key={`skeleton-message-${index}`}
												className={cn(
													"flex",
													index % 2 === 0 ? "justify-start" : "justify-end",
												)}
											>
												<Skeleton className="h-16 w-56 rounded-[17px]" />
											</div>
										))}
									</div>
								) : messages.length === 0 ? (
									<div className="flex h-full flex-col items-center justify-center text-center">
										<div
											className="rounded-full p-4"
											style={{ background: "var(--color-dashboard-divider)" }}
										>
											<MessageSquareText
												className="h-7 w-7"
												style={{ color: "var(--color-dashboard-accent-dark)" }}
											/>
										</div>
										<h4
											className="mt-4 text-xl font-semibold"
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												color: "var(--color-dashboard-text)",
											}}
										>
											Start the first private update
										</h4>
										<p
											className="mt-2 max-w-md text-sm"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											Use this thread for direct coordination with{" "}
											{selectedThread.partner.name}. Comments and project
											activity stay separate.
										</p>
									</div>
								) : (
									<div className="space-y-3">
										{messages.map((message) => (
											<div
												key={message.id}
												className={cn(
													"flex animate-[fade-in_200ms_ease-out]",
													message.isOwnMessage
														? "justify-end"
														: "justify-start",
												)}
											>
												<div
													className={cn(
														"max-w-[82%] rounded-[18px] px-4 py-3 shadow-[0_12px_32px_-24px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_25%,transparent)]",
													)}
													style={
														message.isOwnMessage
															? {
																	background: "var(--color-dashboard-accent)",
																	color: "var(--color-dashboard-on-fill)",
																}
															: {
																	border:
																		"1px solid var(--color-dashboard-border)",
																	background: "var(--color-dashboard-paper)",
																	color: "var(--color-dashboard-text)",
																}
													}
												>
													{message.content && (
														<p className="whitespace-pre-wrap text-sm">
															{message.content}
														</p>
													)}
													{message.attachments.length > 0 && (
														<div className="mt-3 grid grid-cols-2 gap-2">
															{message.attachments.map((attachment) => (
																<img
																	key={attachment.id}
																	src={attachment.fileUrl}
																	alt={
																		attachment.fileName || "Uploaded attachment"
																	}
																	className="h-24 w-full rounded-2xl object-cover"
																/>
															))}
														</div>
													)}
													<p
														className={cn("mt-2 text-[11px]")}
														style={
															message.isOwnMessage
																? {
																		color:
																			"color-mix(in srgb, var(--color-dashboard-on-fill) 70%, transparent)",
																	}
																: { color: "var(--color-dashboard-muted)" }
														}
													>
														{formatTimestamp(message.createdAt)}
													</p>
												</div>
											</div>
										))}
										<div ref={bottomRef} />
									</div>
								)}
							</div>
							<div
								className="border-t p-4 backdrop-blur"
								style={{
									borderColor: "var(--color-dashboard-border)",
									background:
										"color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent)",
								}}
							>
								{attachmentFiles.length > 0 && (
									<div className="mb-3 flex gap-2 overflow-x-auto pb-1">
										{attachmentFiles.map((file, index) => (
											<div
												// biome-ignore lint/suspicious/noArrayIndexKey: File objects have no stable id; names may duplicate
												key={`${file.name}-${index}`}
												className="flex min-w-45 items-center gap-2 rounded-2xl border px-3 py-2"
												style={{
													borderColor: "var(--color-dashboard-border)",
													background: "var(--color-dashboard-divider)",
												}}
											>
												<Paperclip
													className="h-4 w-4"
													style={{ color: "var(--color-dashboard-muted)" }}
												/>
												<span
													className="truncate text-sm"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													{file.name}
												</span>
											</div>
										))}
									</div>
								)}
								<div
									className="rounded-[20px] border p-2 shadow-[0_24px_60px_-38px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_30%,transparent)]"
									style={{
										borderColor: "var(--color-dashboard-border)",
										background: "var(--color-dashboard-paper)",
									}}
								>
									<Textarea
										value={draft}
										onChange={(event) => setDraft(event.target.value)}
										onKeyDown={handleDraftKeyDown}
										placeholder="Write a private update..."
										className="min-h-24 border-0 shadow-none focus-visible:ring-0"
										style={{ background: "transparent" }}
									/>
									<div className="flex items-center justify-between gap-3 px-2 pb-1">
										<label
											className="inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm"
											style={{
												borderColor: "var(--color-dashboard-border)",
												color: "var(--color-dashboard-muted)",
											}}
										>
											<ImagePlus className="h-4 w-4" />
											<span>{isUploading ? "Uploading..." : "Add images"}</span>
											<input
												type="file"
												accept="image/*"
												multiple
												className="hidden"
												onChange={(event) =>
													void handleAttachmentChange(event.target.files)
												}
											/>
										</label>
										<Button
											variant="default"
											onClick={() => void handleSend()}
											disabled={
												(!draft.trim() && uploadedAttachments.length === 0) ||
												isSending ||
												isUploading ||
												!selectedThread?.canSend
											}
											className="rounded-full px-5"
											style={{
												background: "var(--color-dashboard-accent)",
												color: "var(--color-dashboard-on-fill)",
											}}
										>
											<Send className="mr-2 h-4 w-4" />
											Send
										</Button>
									</div>
								</div>
								{sendError && (
									<p
										className="mt-2 text-sm"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									>
										{sendError}
									</p>
								)}
							</div>
						</div>
					) : (
						<div
							className="hidden h-full items-center justify-center md:flex"
							style={{
								background:
									"linear-gradient(135deg, var(--color-dashboard-surface) 0%, var(--color-dashboard-paper) 55%, var(--color-dashboard-surface) 100%)",
							}}
						>
							<div className="max-w-md text-center">
								<div
									className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-dashboard-paper shadow-[0_20px_50px_-35px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_30%,transparent)]"
									style={{ border: "1px solid var(--color-dashboard-border)" }}
								>
									<MessageSquareText
										className="h-8 w-8"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									/>
								</div>
								<h4
									className="mt-6 text-2xl font-semibold"
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										color: "var(--color-dashboard-text)",
									}}
								>
									Choose a conversation
								</h4>
								<p
									className="mt-2 text-sm"
									style={{ color: "var(--color-dashboard-muted)" }}
								>
									Open a direct thread with a designer, contractor, or homeowner
									on this project.
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
